import i18next from "i18next";

import { KNOWN_DOSAGE_FORMS, UNIT_ALIASES } from "@/shared/config/constants";

/** Returns the best short display name for a medicine (trade name preferred). */
export function getMedicineDisplayName(medicine: { trade_name?: string | null; name?: string | null }): string {
  return medicine.trade_name || medicine.name || "-";
}

export function compareMedicinesByDisplayName(
  left: { trade_name?: string | null; name?: string | null },
  right: { trade_name?: string | null; name?: string | null },
): number {
  const leftName = getMedicineDisplayName(left);
  const rightName = getMedicineDisplayName(right);
  const nameComparison = leftName.localeCompare(rightName, undefined, { numeric: true, sensitivity: "base" });
  if (nameComparison !== 0) return nameComparison;

  return String(left.name ?? "").localeCompare(String(right.name ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

/** Translates a dosage form key using i18n if it's a known form. */
export function translateDosageForm(value: string | null | undefined): string | null {
  if (!value) return null;
  const key = String(value).trim().toLowerCase();
  if (!KNOWN_DOSAGE_FORMS.has(key)) return value;
  return i18next.t(`medicines.dosageForms.${key}`, { defaultValue: key });
}

function normalizeLookupKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/['’]+/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function translateContainerType(value: string | null | undefined): string | null {
  if (!value) return null;
  const key = normalizeLookupKey(String(value));
  return i18next.t(`medicines.containerTypes.${key}`, { defaultValue: value });
}

export function translateProductType(value: string | null | undefined): string | null {
  if (!value) return null;
  const key = normalizeLookupKey(String(value));
  return i18next.t(`medicines.productTypes.${key}`, { defaultValue: value });
}

/**
 * Translates strength units (e.g. "10 мг" → "10 mg") using i18n.
 * Handles compound patterns like "10mg/ml" or "10 мг / 5 мл".
 */
export function translateStrength(value: string | null | undefined): string | null {
  if (!value) return null;
  const parts = String(value).split(/\s*([/+])\s*/);
  const translated = parts.map((part) => {
    if (part === "/" || part === "+") return part;
    const trimmed = part.trim();
    if (!trimmed) return trimmed;
    const match = trimmed.match(/^([\d.,]+)\s*(.*)$/);
    if (!match) return trimmed;
    const [, num, suffix] = match;
    if (!suffix) return num;
    const aliasKey = suffix.trim().toLowerCase();
    const canonical = UNIT_ALIASES[aliasKey];
    if (!canonical) return trimmed;
    const unit = i18next.t(`medicines.units.${canonical}`, { defaultValue: aliasKey });
    return `${num} ${unit}`;
  });
  return translated.join(" ").replace(/\s+([/+])\s+/g, " $1 ");
}

/** Builds the "dosageForm · strength · volume · pack" detail line. */
export function getMedicineDetailLine(medicine: {
  strength?: string | null;
  dosage_form?: string | null;
  volume?: string | null;
  pack?: string | number | null;
}): string {
  const strength = translateStrength(medicine.strength);
  const dosageForm = translateDosageForm(medicine.dosage_form);
  const volume = medicine.volume ? String(medicine.volume) : null;
  const pack = medicine.pack != null ? `№${medicine.pack}` : null;

  return [dosageForm, strength, volume, pack].filter(Boolean).join(" · ");
}

export function getMedicineCopyName(
  medicine:
    | {
        name?: string | null;
        strength?: string | null;
        dosage_form?: string | null;
        volume?: string | null;
        pack?: string | number | null;
      }
    | null
    | undefined,
  fallback?: string | null,
): string {
  const canonicalName = medicine?.name?.trim() ?? "";
  return canonicalName || fallback?.trim() || "";
}
